import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, MailIcon, InstagramIcon, LinkedinIcon, GlobeIcon, CheckIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/83gAX3TsGvnbvH5ew774Zt/Metro_Logo_HQ26.png';
/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
function TemplateFrame({
  children,
  format,
  dimensions,
  label,
  aspect = '2/3',
  maxWidth = 'max-w-sm'
}: {
  children: React.ReactNode;
  format: string;
  dimensions: string;
  label: string;
  aspect?: string;
  maxWidth?: string;
}) {
  return <div className="space-y-3" data-id="element-174">
      <div className={`relative ${maxWidth} mx-auto w-full`} data-id="element-175">
        <div className="rounded-sm border border-[var(--divider)] overflow-hidden shadow-sm" style={{
        aspectRatio: aspect
      }} data-id="element-176">
          {children}
        </div>
      </div>
      <div className="text-center" data-id="element-177">
        <div className="font-space text-sm font-medium" data-id="element-178">{label}</div>
        <div className="font-mono text-[10px] text-[var(--text-muted)] mt-1" data-id="element-179">
          {format} · {dimensions}
        </div>
      </div>
    </div>;
}
function QRPlaceholder({
  size = 60,
  dark = false
}: {
  size?: number;
  dark?: boolean;
}) {
  const cells = 21;
  const cell = size / cells;
  const fg = dark ? '#FFFFFF' : '#000000';
  const bg = dark ? 'transparent' : '#FFFFFF';
  const inFinder = (x: number, y: number) => x < 7 && y < 7 || x >= cells - 7 && y < 7 || x < 7 && y >= cells - 7;
  const finderFilled = (x: number, y: number) => {
    let lx = x,
      ly = y;
    if (x >= cells - 7 && y < 7) lx = x - (cells - 7);
    if (x < 7 && y >= cells - 7) ly = y - (cells - 7);
    const isEdge = lx === 0 || lx === 6 || ly === 0 || ly === 6;
    const isCore = lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4;
    return isEdge || isCore;
  };
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{
    background: bg,
    display: 'block'
  }} aria-label="QR code placeholder" data-id="element-180">
      {Array.from({
      length: cells
    }).flatMap((_, y) => Array.from({
      length: cells
    }).map((_, x) => {
      let fill = false;
      if (inFinder(x, y)) {
        fill = finderFilled(x, y);
      } else {
        fill = (x * 13 + y * 7 + x * y * 3) % 5 < 2;
      }
      return fill ? <rect key={`${x}-${y}`} x={x * cell} y={y * cell} width={cell} height={cell} fill={fg} data-id="element-181" /> : null;
    }))}
    </svg>;
}
function ScanBlock({
  size = 48,
  dark = false,
  label = 'metroagency.co'
}: {
  size?: number;
  dark?: boolean;
  label?: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-182">
      <div className="rounded-[2px] overflow-hidden flex-shrink-0" style={{
      background: dark ? 'transparent' : '#FFFFFF',
      padding: dark ? 0 : 2
    }} data-id="element-183">
        <QRPlaceholder size={size} dark={dark} data-id="element-184" />
      </div>
      <div className="flex flex-col" data-id="element-185">
        <div className={`font-mono text-[7px] uppercase tracking-wider ${dark ? 'text-white/60' : 'text-[var(--text-muted)]'}`} data-id="element-186">
          Scan
        </div>
        <div className={`font-space text-[9px] font-medium ${dark ? 'text-white' : 'text-[var(--text-ink)]'}`} data-id="element-187">
          {label}
        </div>
      </div>
    </div>;
}
function ServiceNetworkSVG({
  highlightLabel,
  accentColor = 'var(--accent-blue)'
}: {
  highlightLabel?: string;
  accentColor?: string;
}) {
  return <svg viewBox="0 0 320 200" className="w-full h-full" data-id="element-188">
      <path d="M 10 50 L 90 50 L 110 70 L 200 70 L 220 50 L 310 50" fill="none" stroke="var(--accent-blue)" strokeWidth="2.5" strokeLinecap="round" data-id="element-189" />
      <path d="M 10 100 L 80 100 L 100 120 L 220 120 L 240 100 L 310 100" fill="none" stroke="var(--accent-yellow)" strokeWidth="2.5" strokeLinecap="round" data-id="element-190" />
      <path d="M 10 150 L 130 150 L 150 130 L 260 130 L 280 150 L 310 150" fill="none" stroke="var(--accent-orange)" strokeWidth="2.5" strokeLinecap="round" data-id="element-191" />
      <path d="M 60 20 L 60 50 L 80 70 L 80 180" fill="none" stroke="var(--accent-green)" strokeWidth="2.5" strokeLinecap="round" data-id="element-192" />
      {['Strategy', 'Creative', 'Web', 'Performance'].map((label, i) => {
      const positions = [[110, 70], [200, 70], [220, 120], [260, 130]];
      const [x, y] = positions[i];
      const isHighlight = label === highlightLabel;
      return <g key={label} data-id="element-193">
            <circle cx={x} cy={y} r={isHighlight ? 7 : 4} fill="white" stroke={isHighlight ? accentColor : 'var(--text-ink)'} strokeWidth={isHighlight ? 3 : 2} data-id="element-194" />
          </g>;
    })}
    </svg>;
}
/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */
export function ContentTemplatesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="templates" ref={ref} className="min-h-screen px-8 py-24" data-id="element-195">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-196">
        <div className="mb-16" data-id="element-197">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-198">
            08 / CONTENT TEMPLATES
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-199">
            Templates in Context
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 max-w-2xl font-helvetica" data-id="element-200">
            Production-ready surfaces in the brand system. Five formats, three
            directions each — ready for the team to react to and slot real copy
            into. Print pieces route to metroagency.co via QR; email pieces use
            real CTAs.
          </p>
        </div>

        <div className="space-y-24" data-id="element-201">
          {/* 08.1 — Brand Street Posters */}
          <div data-id="element-202">
            <div className="flex items-baseline justify-between mb-8" data-id="element-203">
              <div data-id="element-204">
                <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-1" data-id="element-205">
                  08.1 — BRAND POSTER
                </div>
                <h3 className="font-space text-3xl font-medium" data-id="element-206">
                  Street Poster · Brand
                </h3>
                <p className="font-helvetica text-sm text-[var(--text-muted)] mt-2 max-w-xl" data-id="element-207">
                  Designed to live in the street. Brand-led, no service
                  messaging. Three takes — network, type, color.
                </p>
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] text-right" data-id="element-208">
                A2 · 420×594mm
                <br data-id="element-209" />
                portrait
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-210">
              <TemplateFrame format="A2 Portrait" dimensions="420×594mm" label="A · Network" data-id="element-211">
                <div className="w-full h-full bg-[var(--bg-canvas)] p-7 flex flex-col" data-id="element-212">
                  <div className="flex items-start justify-between mb-auto" data-id="element-213">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-28 h-auto" data-id="element-214" />
                    <div className="font-mono text-[8px] text-[var(--text-muted)] text-right leading-relaxed" data-id="element-215">
                      EST · 2023
                      <br data-id="element-216" />
                      BCN · ES
                    </div>
                  </div>
                  <div className="flex-1 flex items-center justify-center -mx-2 my-4" data-id="element-217">
                    <ServiceNetworkSVG data-id="element-218" />
                  </div>
                  <div className="font-space text-2xl font-medium leading-[1.1] text-[var(--accent-blue)]" data-id="element-219">
                    A network
                    <br data-id="element-220" />
                    of ideas.
                  </div>
                  <div className="h-px bg-[var(--text-ink)] mt-4 mb-3 w-10" data-id="element-221" />
                  <div className="flex items-end justify-between gap-3" data-id="element-222">
                    <div className="font-helvetica text-[9px] text-[var(--text-muted)] leading-relaxed" data-id="element-223">
                      <div data-id="element-224">metroagency.co</div>
                      <div data-id="element-225">@metroagency</div>
                    </div>
                    <ScanBlock size={42} data-id="element-226" />
                  </div>
                </div>
              </TemplateFrame>

              <TemplateFrame format="A2 Portrait" dimensions="420×594mm" label="B · Type" data-id="element-227">
                <div className="w-full h-full bg-[var(--accent-blue)] p-7 flex flex-col text-white relative overflow-hidden" data-id="element-228">
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-229">
                    <div className="font-space font-bold text-[78px] leading-[0.85] tracking-tighter text-center" data-id="element-230">
                      A<br data-id="element-231" />
                      NETWORK
                      <br data-id="element-232" />
                      OF IDEAS.
                    </div>
                  </div>
                  <div className="relative z-10 flex items-start justify-between" data-id="element-233">
                    <div className="w-3 h-3 border-t-2 border-l-2 border-white" data-id="element-234" />
                    <div className="w-3 h-3 border-t-2 border-r-2 border-white" data-id="element-235" />
                  </div>
                  <div className="relative z-10 mt-auto flex items-end justify-between gap-3" data-id="element-236">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-20 h-auto" style={{
                    filter: 'brightness(0) invert(1)'
                  }} data-id="element-237" />
                    <div className="text-right" data-id="element-238">
                      <div className="font-mono text-[8px] opacity-70 leading-relaxed mb-1" data-id="element-239">
                        THE HANDS-ON
                        <br data-id="element-240" />
                        AGENCY
                      </div>
                      <div className="font-helvetica text-[9px] opacity-90" data-id="element-241">
                        metroagency.co
                      </div>
                    </div>
                  </div>
                </div>
              </TemplateFrame>

              <TemplateFrame format="A2 Portrait" dimensions="420×594mm" label="C · Color Block" data-id="element-242">
                <div className="w-full h-full flex flex-col" data-id="element-243">
                  <div className="flex-1 grid grid-cols-5 gap-0" data-id="element-244">
                    <div style={{
                    background: 'var(--accent-blue)'
                  }} data-id="element-245" />
                    <div style={{
                    background: 'var(--accent-yellow)'
                  }} data-id="element-246" />
                    <div style={{
                    background: 'var(--accent-orange)'
                  }} data-id="element-247" />
                    <div style={{
                    background: 'var(--accent-green)'
                  }} data-id="element-248" />
                    <div style={{
                    background: 'var(--accent-amber)'
                  }} data-id="element-249" />
                  </div>
                  <div className="bg-[var(--bg-canvas)] p-6 flex items-end justify-between gap-4" data-id="element-250">
                    <div className="flex flex-col gap-2" data-id="element-251">
                      <img src={METRO_LOGO_URL} alt="me—tro" className="w-28 h-auto" data-id="element-252" />
                      <div className="font-space text-base font-medium leading-tight" data-id="element-253">
                        Five lines.
                        <br data-id="element-254" />
                        One network.
                      </div>
                      <div className="font-mono text-[9px] text-[var(--text-muted)] mt-1" data-id="element-255">
                        BARCELONA · 2026
                      </div>
                    </div>
                    <ScanBlock size={44} data-id="element-256" />
                  </div>
                </div>
              </TemplateFrame>
            </div>
          </div>

          {/* 08.2 — Web Design Street Poster */}
          <div data-id="element-257">
            <div className="flex items-baseline justify-between mb-8" data-id="element-258">
              <div data-id="element-259">
                <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-1" data-id="element-260">
                  08.2 — SERVICE POSTER
                </div>
                <h3 className="font-space text-3xl font-medium" data-id="element-261">
                  Street Poster · Web Design
                </h3>
                <p className="font-helvetica text-sm text-[var(--text-muted)] mt-2 max-w-xl" data-id="element-262">
                  Service-led. Promotes web design specifically. Three takes —
                  network, process, window.
                </p>
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] text-right" data-id="element-263">
                A3 · 297×420mm
                <br data-id="element-264" />
                portrait
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-265">
              <TemplateFrame format="A3 Portrait" dimensions="297×420mm" label="A · Highlighted Network" data-id="element-266">
                <div className="w-full h-full bg-[var(--bg-canvas)] p-6 flex flex-col" data-id="element-267">
                  <div className="flex items-start justify-between mb-5" data-id="element-268">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-20 h-auto" data-id="element-269" />
                    <div className="px-2 py-0.5 rounded-full bg-[var(--text-ink)] text-white font-mono text-[8px] tracking-wider" data-id="element-270">
                      SERVICE · 03
                    </div>
                  </div>
                  <div data-id="element-271">
                    <div className="font-space text-3xl font-bold tracking-tight leading-[0.95]" data-id="element-272">
                      WE BUILD
                      <br data-id="element-273" />
                      WEBSITES
                      <br data-id="element-274" />
                      <span className="text-[var(--accent-blue)]" data-id="element-275">
                        THAT WORK.
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center -mx-2 my-4" data-id="element-276">
                    <ServiceNetworkSVG highlightLabel="Web" accentColor="var(--accent-blue)" data-id="element-277" />
                  </div>
                  <div className="border-t border-[var(--text-ink)] pt-4 flex items-end justify-between gap-3" data-id="element-278">
                    <div className="space-y-1" data-id="element-279">
                      <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-wider" data-id="element-280">
                        VISIT
                      </div>
                      <div className="font-space text-sm font-medium" data-id="element-281">
                        metroagency.co/web
                      </div>
                      <div className="font-helvetica text-[9px] text-[var(--text-muted)]" data-id="element-282">
                        Barcelona · EST 2023
                      </div>
                    </div>
                    <ScanBlock size={48} data-id="element-283" />
                  </div>
                </div>
              </TemplateFrame>

              <TemplateFrame format="A3 Portrait" dimensions="297×420mm" label="B · Process" data-id="element-284">
                <div className="w-full h-full bg-white p-6 flex flex-col" data-id="element-285">
                  <div className="flex items-start justify-between mb-5" data-id="element-286">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-20 h-auto" data-id="element-287" />
                    <div className="w-3 h-3 border-t-2 border-r-2 border-[var(--text-ink)]" data-id="element-288" />
                  </div>
                  <div className="font-space text-3xl font-bold tracking-tight mb-6 leading-[0.95]" data-id="element-289">
                    WEB
                    <br data-id="element-290" />
                    DESIGN.
                    <br data-id="element-291" />
                    <span className="text-[var(--accent-orange)] italic font-display-condensed font-normal" data-id="element-292">
                      in four moves.
                    </span>
                  </div>
                  <div className="space-y-1.5" data-id="element-293">
                    {[{
                    n: '01',
                    t: 'Brief',
                    d: 'We listen, you talk.'
                  }, {
                    n: '02',
                    t: 'Design',
                    d: 'We draft, you react.'
                  }, {
                    n: '03',
                    t: 'Build',
                    d: 'We code, you preview.'
                  }, {
                    n: '04',
                    t: 'Ship',
                    d: 'We launch, you grow.'
                  }].map(s => <div key={s.n} className="flex items-center gap-3 py-2 border-b border-[var(--divider)]" data-id="element-294">
                        <div className="font-mono text-[10px] text-[var(--accent-blue)] w-6" data-id="element-295">
                          {s.n}
                        </div>
                        <div className="font-space font-medium text-sm flex-1" data-id="element-296">
                          {s.t}
                        </div>
                        <div className="font-helvetica text-[10px] text-[var(--text-muted)]" data-id="element-297">
                          {s.d}
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-auto pt-4 border-t border-[var(--text-ink)] flex items-end justify-between gap-3" data-id="element-298">
                    <div className="space-y-1" data-id="element-299">
                      <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-wider" data-id="element-300">
                        SCAN OR VISIT
                      </div>
                      <div className="font-space text-sm font-medium" data-id="element-301">
                        metroagency.co
                      </div>
                    </div>
                    <ScanBlock size={48} data-id="element-302" />
                  </div>
                </div>
              </TemplateFrame>

              <TemplateFrame format="A3 Portrait" dimensions="297×420mm" label="C · Window UI" data-id="element-303">
                <div className="w-full h-full bg-[var(--bg-canvas)] p-5 flex flex-col" data-id="element-304">
                  <div className="bg-white rounded-md border border-[var(--divider)] flex-1 flex flex-col overflow-hidden" data-id="element-305">
                    <div className="bg-[var(--bg-canvas)] border-b border-[var(--divider)] px-3 py-2 flex items-center gap-1.5" data-id="element-306">
                      <div className="w-2 h-2 rounded-full bg-[var(--accent-orange)]" data-id="element-307" />
                      <div className="w-2 h-2 rounded-full bg-[var(--accent-yellow)]" data-id="element-308" />
                      <div className="w-2 h-2 rounded-full bg-[var(--accent-green)]" data-id="element-309" />
                      <div className="flex-1 mx-2 h-3 bg-white rounded text-[7px] text-center text-[var(--text-muted)] flex items-center justify-center font-mono" data-id="element-310">
                        metroagency.co/web
                      </div>
                    </div>
                    <div className="flex-1 p-5 flex flex-col" data-id="element-311">
                      <div className="font-space text-2xl font-bold tracking-tight leading-[0.95] mb-3" data-id="element-312">
                        Websites
                        <br data-id="element-313" />
                        that ship.
                      </div>
                      <div className="font-helvetica text-[10px] text-[var(--text-muted)] leading-relaxed mb-5" data-id="element-314">
                        Strategy, design, and build in one place. From the first
                        wireframe to launch day.
                      </div>
                      <div className="space-y-1.5 font-mono text-[9px] text-[var(--text-muted)] flex-1" data-id="element-315">
                        <div data-id="element-316">&gt; design system · ready</div>
                        <div data-id="element-317">&gt; components · ready</div>
                        <div data-id="element-318">&gt; performance · 98 lighthouse</div>
                        <div data-id="element-319">&gt; ship date · 6 weeks</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-end justify-between mt-4 gap-3" data-id="element-320">
                    <div className="space-y-1" data-id="element-321">
                      <img src={METRO_LOGO_URL} alt="me—tro" className="w-16 h-auto mb-1" data-id="element-322" />
                      <div className="font-mono text-[8px] text-[var(--text-muted)]" data-id="element-323">
                        WEB DESIGN · v2026
                      </div>
                    </div>
                    <ScanBlock size={44} data-id="element-324" />
                  </div>
                </div>
              </TemplateFrame>
            </div>
          </div>

          {/* 08.3 — Email Templates — MODERN REBUILD */}
          <div data-id="element-325">
            <div className="flex items-baseline justify-between mb-8" data-id="element-326">
              <div data-id="element-327">
                <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-1" data-id="element-328">
                  08.3 — EMAIL
                </div>
                <h3 className="font-space text-3xl font-medium" data-id="element-329">
                  Email Templates
                </h3>
                <p className="font-helvetica text-sm text-[var(--text-muted)] mt-2 max-w-xl" data-id="element-330">
                  Built for the inbox — single-column responsive, real preheader
                  text, sender meta, proper footers with unsubscribe and
                  preferences. Three jobs: dispatch newsletter, promo blast,
                  service announcement.
                </p>
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] text-right" data-id="element-331">
                600px wide
                <br data-id="element-332" />
                responsive · HTML
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-333">
              {/* A — Newsletter / Dispatch */}
              <TemplateFrame format="HTML Email" dimensions="600px · responsive" label="A · Dispatch (Newsletter)" aspect="3/5" data-id="element-334">
                <div className="w-full h-full bg-[#F4F5F7] flex flex-col overflow-hidden text-[var(--text-ink)]" data-id="element-335">
                  {/* Inbox-style header bar */}
                  <div className="bg-[#F4F5F7] px-4 pt-3 pb-1 flex items-center justify-between" data-id="element-336">
                    <div className="font-mono text-[7px] text-[var(--text-muted)]" data-id="element-337">
                      From: hello@metroagency.co
                    </div>
                    <div className="font-mono text-[7px] text-[var(--text-muted)] underline" data-id="element-338">
                      View in browser
                    </div>
                  </div>
                  {/* Email container card */}
                  <div className="mx-3 mb-3 flex-1 bg-white rounded-md border border-[var(--divider)] flex flex-col overflow-hidden" data-id="element-339">
                    {/* Logo header */}
                    <div className="px-5 pt-5 pb-4 border-b border-[var(--divider)] flex items-center justify-between" data-id="element-340">
                      <img src={METRO_LOGO_URL} alt="me—tro" className="h-4 w-auto" data-id="element-341" />
                      <div className="font-mono text-[7px] text-[var(--text-muted)]" data-id="element-342">
                        DISPATCH · 014
                      </div>
                    </div>
                    {/* Hero */}
                    <div className="px-5 pt-5 pb-3" data-id="element-343">
                      <div className="font-space font-bold text-[18px] leading-[1.15] tracking-tight" data-id="element-344">
                        What we shipped this quarter.
                      </div>
                      <div className="font-helvetica text-[9px] text-[var(--text-muted)] mt-2 leading-relaxed" data-id="element-345">
                        Three projects, two awards, one new hire. A two-minute
                        read.
                      </div>
                    </div>
                    {/* Hero image */}
                    <div className="mx-5 aspect-[16/9] rounded bg-[var(--accent-blue)] flex items-center justify-center mb-4 overflow-hidden" data-id="element-346">
                      <ServiceNetworkSVG data-id="element-347" />
                    </div>
                    {/* Article cards */}
                    <div className="px-5 space-y-3" data-id="element-348">
                      {[{
                      tag: 'CASE STUDY',
                      t: 'Shop redesign · Barcelona florist',
                      d: '6 weeks, +40% conversions.'
                    }, {
                      tag: 'PROJECT',
                      t: 'New brand · fintech startup',
                      d: 'From zero to launch.'
                    }, {
                      tag: 'NUMBERS',
                      t: 'Performance campaign · +43% lift',
                      d: 'Q1 in three numbers.'
                    }].map((a, i) => <a key={i} className="flex items-center gap-3 pb-3 border-b border-[var(--divider)] last:border-0 cursor-pointer" data-id="element-349">
                          <div className="w-10 h-10 rounded bg-[var(--bg-canvas)] flex-shrink-0 flex items-center justify-center" data-id="element-350">
                            <div className="font-mono text-[8px] text-[var(--accent-blue)]" data-id="element-351">
                              0{i + 1}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-352">
                            <div className="font-mono text-[7px] text-[var(--text-muted)] mb-0.5" data-id="element-353">
                              {a.tag}
                            </div>
                            <div className="font-space text-[10px] font-medium leading-tight truncate" data-id="element-354">
                              {a.t}
                            </div>
                            <div className="font-helvetica text-[8px] text-[var(--text-muted)] truncate" data-id="element-355">
                              {a.d}
                            </div>
                          </div>
                          <ArrowRightIcon className="w-2.5 h-2.5 text-[var(--text-muted)] flex-shrink-0" data-id="element-356" />
                        </a>)}
                    </div>
                    {/* CTA */}
                    <div className="px-5 py-4 flex items-center justify-center" data-id="element-357">
                      <a className="px-4 py-2 bg-[var(--accent-blue)] text-white rounded-full font-helvetica text-[10px] font-medium inline-flex items-center gap-1.5" data-id="element-358">
                        Read the full dispatch{' '}
                        <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-359" />
                      </a>
                    </div>
                    {/* Footer */}
                    <div className="mt-auto bg-[var(--bg-canvas)] px-5 py-4 space-y-2.5 border-t border-[var(--divider)]" data-id="element-360">
                      <div className="flex items-center gap-2" data-id="element-361">
                        <InstagramIcon className="w-2.5 h-2.5 text-[var(--text-muted)]" data-id="element-362" />
                        <LinkedinIcon className="w-2.5 h-2.5 text-[var(--text-muted)]" data-id="element-363" />
                        <GlobeIcon className="w-2.5 h-2.5 text-[var(--text-muted)]" data-id="element-364" />
                      </div>
                      <div className="font-helvetica text-[7px] text-[var(--text-muted)] leading-relaxed" data-id="element-365">
                        Metro Agency · Carrer de la Marina, Barcelona, ES ·
                        You're getting this because you subscribed at
                        metroagency.co.
                      </div>
                      <div className="flex items-center gap-3 font-mono text-[7px] text-[var(--text-muted)]" data-id="element-366">
                        <a className="underline" data-id="element-367">Manage preferences</a>
                        <a className="underline" data-id="element-368">Unsubscribe</a>
                      </div>
                    </div>
                  </div>
                </div>
              </TemplateFrame>

              {/* B — Promo Blast */}
              <TemplateFrame format="HTML Email" dimensions="600px · responsive" label="B · Promo Blast" aspect="3/5" data-id="element-369">
                <div className="w-full h-full bg-[#F4F5F7] flex flex-col overflow-hidden" data-id="element-370">
                  <div className="bg-[#F4F5F7] px-4 pt-3 pb-1 flex items-center justify-between" data-id="element-371">
                    <div className="font-mono text-[7px] text-[var(--text-muted)]" data-id="element-372">
                      Limited offer · ends March 31
                    </div>
                    <div className="font-mono text-[7px] text-[var(--text-muted)] underline" data-id="element-373">
                      View in browser
                    </div>
                  </div>
                  <div className="mx-3 mb-3 flex-1 bg-white rounded-md border border-[var(--divider)] flex flex-col overflow-hidden" data-id="element-374">
                    <div className="px-5 pt-5 pb-4 flex items-center justify-center" data-id="element-375">
                      <img src={METRO_LOGO_URL} alt="me—tro" className="h-4 w-auto" data-id="element-376" />
                    </div>
                    {/* Hero offer block */}
                    <div className="mx-3 bg-[var(--accent-blue)] text-white rounded-md p-6 flex flex-col items-center text-center mb-5" data-id="element-377">
                      <div className="px-2 py-0.5 rounded-full bg-white/15 font-mono text-[7px] tracking-widest mb-3" data-id="element-378">
                        LIMITED · TO MARCH 31
                      </div>
                      <div className="font-space font-bold text-[28px] leading-[0.9] tracking-tighter mb-3" data-id="element-379">
                        A new website,
                        <br data-id="element-380" />
                        in six weeks.
                      </div>
                      <div className="font-helvetica text-[9px] opacity-90 leading-relaxed max-w-[180px] mb-5" data-id="element-381">
                        Brief us by month-end and we ship before summer. Fixed
                        scope, fixed price.
                      </div>
                      <a className="px-4 py-2 bg-white text-[var(--accent-blue)] rounded-full font-helvetica text-[10px] font-medium inline-flex items-center gap-1.5" data-id="element-382">
                        Book a call <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-383" />
                      </a>
                    </div>
                    {/* Benefits */}
                    <div className="px-5 space-y-2.5" data-id="element-384">
                      {['Strategy, design, build — under one roof', 'Fixed scope · fixed price · fixed date', '98+ Lighthouse score, guaranteed'].map((b, i) => <div key={i} className="flex items-start gap-2" data-id="element-385">
                          <div className="w-3.5 h-3.5 rounded-full bg-[var(--bg-canvas)] flex-shrink-0 flex items-center justify-center mt-0.5" data-id="element-386">
                            <CheckIcon className="w-2 h-2 text-[var(--accent-blue)]" strokeWidth={3} data-id="element-387" />
                          </div>
                          <div className="font-helvetica text-[9px] leading-relaxed" data-id="element-388">
                            {b}
                          </div>
                        </div>)}
                    </div>
                    {/* Social proof */}
                    <div className="mx-5 mt-5 py-3 border-t border-b border-[var(--divider)] text-center" data-id="element-389">
                      <div className="font-mono text-[7px] text-[var(--text-muted)] tracking-wider mb-1" data-id="element-390">
                        TRUSTED BY
                      </div>
                      <div className="font-space text-[10px] font-medium" data-id="element-391">
                        50+ teams across Europe
                      </div>
                    </div>
                    {/* Secondary CTA + Footer */}
                    <div className="mt-auto px-5 pt-4" data-id="element-392">
                      <div className="text-center" data-id="element-393">
                        <a className="font-mono text-[8px] text-[var(--accent-blue)] underline" data-id="element-394">
                          Or reply to this email — we read every one.
                        </a>
                      </div>
                    </div>
                    <div className="mt-3 bg-[var(--bg-canvas)] px-5 py-3 space-y-1.5 border-t border-[var(--divider)]" data-id="element-395">
                      <div className="font-helvetica text-[7px] text-[var(--text-muted)] leading-relaxed" data-id="element-396">
                        Metro Agency · Barcelona, ES · hello@metroagency.co
                      </div>
                      <div className="flex items-center gap-3 font-mono text-[7px] text-[var(--text-muted)]" data-id="element-397">
                        <a className="underline" data-id="element-398">Preferences</a>
                        <a className="underline" data-id="element-399">Unsubscribe</a>
                      </div>
                    </div>
                  </div>
                </div>
              </TemplateFrame>

              {/* C — Service Launch / Transactional */}
              <TemplateFrame format="HTML Email" dimensions="600px · responsive" label="C · Service Launch" aspect="3/5" data-id="element-400">
                <div className="w-full h-full bg-[#F4F5F7] flex flex-col overflow-hidden text-[var(--text-ink)]" data-id="element-401">
                  <div className="bg-[#F4F5F7] px-4 pt-3 pb-1 flex items-center justify-between" data-id="element-402">
                    <div className="font-mono text-[7px] text-[var(--text-muted)]" data-id="element-403">
                      From: ana@metroagency.co
                    </div>
                    <div className="font-mono text-[7px] text-[var(--text-muted)] underline" data-id="element-404">
                      View in browser
                    </div>
                  </div>
                  <div className="mx-3 mb-3 flex-1 bg-white rounded-md border border-[var(--divider)] flex flex-col overflow-hidden" data-id="element-405">
                    <div className="px-5 pt-5 pb-3 flex items-center justify-between" data-id="element-406">
                      <img src={METRO_LOGO_URL} alt="me—tro" className="h-4 w-auto" data-id="element-407" />
                      <div className="px-2 py-0.5 rounded-full bg-[var(--accent-blue)]/10 text-[var(--accent-blue)] font-mono text-[7px] tracking-wider" data-id="element-408">
                        NEW · SERVICE
                      </div>
                    </div>
                    <div className="px-5 py-3" data-id="element-409">
                      <div className="font-helvetica text-[10px] mb-3" data-id="element-410">
                        Hey {'{{ first_name }}'} —
                      </div>
                      <div className="font-space font-bold text-[20px] leading-[1.1] tracking-tight mb-3" data-id="element-411">
                        Introducing
                        <br data-id="element-412" />
                        Brand Sprints.
                      </div>
                      <div className="font-helvetica text-[9px] text-[var(--text-muted)] leading-relaxed" data-id="element-413">
                        Two weeks. One team. A complete brand system you can run
                        with from day one.
                      </div>
                    </div>
                    {/* Feature card */}
                    <div className="mx-5 my-3 bg-[var(--bg-canvas)] rounded-md p-4" data-id="element-414">
                      <div className="font-mono text-[7px] text-[var(--text-muted)] mb-3 tracking-wider" data-id="element-415">
                        WHAT'S INCLUDED
                      </div>
                      <div className="space-y-2" data-id="element-416">
                        {['Positioning & messaging workshop', 'Visual identity & guidelines', 'Launch assets · web, social, print', 'A 30-day support window'].map(f => <div key={f} className="flex items-start gap-2" data-id="element-417">
                            <CheckIcon className="w-2.5 h-2.5 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={3} data-id="element-418" />
                            <div className="font-helvetica text-[9px] leading-relaxed" data-id="element-419">
                              {f}
                            </div>
                          </div>)}
                      </div>
                    </div>
                    {/* CTA */}
                    <div className="px-5 py-3 flex justify-center" data-id="element-420">
                      <a className="px-5 py-2 bg-[var(--text-ink)] text-white rounded-full font-helvetica text-[10px] font-medium inline-flex items-center gap-1.5" data-id="element-421">
                        See how it works{' '}
                        <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-422" />
                      </a>
                    </div>
                    {/* Signature */}
                    <div className="px-5 pb-3 mt-2" data-id="element-423">
                      <div className="font-helvetica text-[9px] text-[var(--text-muted)] leading-relaxed" data-id="element-424">
                        Reply if you have a question — I read every one.
                      </div>
                      <div className="mt-3 space-y-0.5" data-id="element-425">
                        <div className="font-space text-[10px] font-medium" data-id="element-426">
                          Ana M.
                        </div>
                        <div className="font-helvetica text-[8px] text-[var(--text-muted)]" data-id="element-427">
                          Creative Director · Metro
                        </div>
                      </div>
                    </div>
                    {/* PS */}
                    <div className="mx-5 mb-3 px-3 py-2 border-l-2 border-[var(--accent-blue)] bg-[var(--bg-canvas)]/50" data-id="element-428">
                      <div className="font-helvetica text-[8px] text-[var(--text-muted)] leading-relaxed italic" data-id="element-429">
                        P.S. First three sprints booked before April get a free
                        brand audit on us.
                      </div>
                    </div>
                    {/* Footer */}
                    <div className="mt-auto bg-[var(--bg-canvas)] px-5 py-3 space-y-1.5 border-t border-[var(--divider)]" data-id="element-430">
                      <div className="font-helvetica text-[7px] text-[var(--text-muted)]" data-id="element-431">
                        Metro Agency · Barcelona, ES · metroagency.co
                      </div>
                      <div className="flex items-center gap-3 font-mono text-[7px] text-[var(--text-muted)]" data-id="element-432">
                        <a className="underline" data-id="element-433">Preferences</a>
                        <a className="underline" data-id="element-434">Unsubscribe</a>
                      </div>
                    </div>
                  </div>
                </div>
              </TemplateFrame>
            </div>
          </div>

          {/* 08.4 — Postcard A5 */}
          <div data-id="element-435">
            <div className="flex items-baseline justify-between mb-8" data-id="element-436">
              <div data-id="element-437">
                <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-1" data-id="element-438">
                  08.4 — POSTCARD
                </div>
                <h3 className="font-space text-3xl font-medium" data-id="element-439">
                  Postcard · Brand Promo
                </h3>
                <p className="font-helvetica text-sm text-[var(--text-muted)] mt-2 max-w-xl" data-id="element-440">
                  A5 landscape. Quick to hand out at events, leave on a counter,
                  slip into a folder.
                </p>
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] text-right" data-id="element-441">
                A5 · 148×210mm
                <br data-id="element-442" />
                landscape
              </div>
            </div>

            <div className="space-y-12" data-id="element-443">
              <div data-id="element-444">
                <div className="font-mono text-[10px] text-[var(--text-muted)] mb-4" data-id="element-445">
                  A · FRONT/BACK
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-446">
                  <TemplateFrame format="A5 Landscape" dimensions="210×148mm" label="Front" aspect="210/148" maxWidth="max-w-md" data-id="element-447">
                    <div className="w-full h-full bg-[var(--accent-blue)] flex items-center justify-center relative px-6" data-id="element-448">
                      <img src={METRO_LOGO_URL} alt="me—tro" className="w-2/3 h-auto" style={{
                      filter: 'brightness(0) invert(1)'
                    }} data-id="element-449" />
                      <div className="absolute bottom-4 left-4 font-mono text-[8px] text-white/60" data-id="element-450">
                        BARCELONA · 2026
                      </div>
                      <div className="absolute bottom-4 right-4 font-mono text-[8px] text-white/60" data-id="element-451">
                        THE HANDS-ON AGENCY
                      </div>
                    </div>
                  </TemplateFrame>
                  <TemplateFrame format="A5 Landscape" dimensions="210×148mm" label="Back" aspect="210/148" maxWidth="max-w-md" data-id="element-452">
                    <div className="w-full h-full bg-[var(--bg-canvas)] p-5 flex flex-col" data-id="element-453">
                      <div className="flex items-start justify-between mb-3" data-id="element-454">
                        <img src={METRO_LOGO_URL} alt="me—tro" className="w-16 h-auto" data-id="element-455" />
                        <div className="font-mono text-[8px] text-[var(--text-muted)] text-right leading-relaxed" data-id="element-456">
                          A NETWORK
                          <br data-id="element-457" />
                          OF IDEAS
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 my-auto" data-id="element-458">
                        {['Strategy', 'Brand', 'Web', 'Content', 'Video', 'Performance'].map(s => <div key={s} className="flex items-center gap-1.5 py-1 border-b border-[var(--divider)]" data-id="element-459">
                            <div className="w-1 h-1 rounded-full bg-[var(--accent-blue)]" data-id="element-460" />
                            <div className="font-helvetica text-[10px]" data-id="element-461">
                              {s}
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-auto pt-3 border-t border-[var(--text-ink)] flex items-end justify-between gap-3" data-id="element-462">
                        <div className="space-y-1 font-mono text-[8px] text-[var(--text-muted)]" data-id="element-463">
                          <div className="flex items-center gap-1" data-id="element-464">
                            <MapPinIcon className="w-2 h-2" data-id="element-465" />
                            Barcelona, ES
                          </div>
                          <div className="flex items-center gap-1" data-id="element-466">
                            <MailIcon className="w-2 h-2" data-id="element-467" />
                            hello@metroagency.co
                          </div>
                          <div className="flex items-center gap-1" data-id="element-468">
                            <GlobeIcon className="w-2 h-2" data-id="element-469" />
                            metroagency.co
                          </div>
                        </div>
                        <ScanBlock size={44} data-id="element-470" />
                      </div>
                    </div>
                  </TemplateFrame>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-471">
                <div data-id="element-472">
                  <div className="font-mono text-[10px] text-[var(--text-muted)] mb-4" data-id="element-473">
                    B · SERVICE GRID
                  </div>
                  <TemplateFrame format="A5 Landscape" dimensions="210×148mm" label="Single side" aspect="210/148" maxWidth="max-w-md" data-id="element-474">
                    <div className="w-full h-full bg-white p-5 flex flex-col" data-id="element-475">
                      <div className="flex items-start justify-between mb-3" data-id="element-476">
                        <img src={METRO_LOGO_URL} alt="me—tro" className="w-16 h-auto" data-id="element-477" />
                        <div className="font-space text-[10px] font-medium text-right leading-tight" data-id="element-478">
                          The hands-on
                          <br data-id="element-479" />
                          agency.
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 flex-1" data-id="element-480">
                        {[{
                        t: 'Brand',
                        c: 'var(--accent-blue)'
                      }, {
                        t: 'Web',
                        c: 'var(--accent-yellow)'
                      }, {
                        t: 'Content',
                        c: 'var(--accent-orange)'
                      }, {
                        t: 'Performance',
                        c: 'var(--accent-green)'
                      }].map(s => <div key={s.t} className="rounded-sm flex items-end p-2 text-white font-space text-xs font-medium" style={{
                        background: s.c
                      }} data-id="element-481">
                            {s.t}
                          </div>)}
                      </div>
                      <div className="mt-3 pt-2 border-t border-[var(--divider)] flex items-end justify-between gap-3" data-id="element-482">
                        <div className="font-mono text-[8px] text-[var(--text-muted)] space-y-0.5" data-id="element-483">
                          <div data-id="element-484">metroagency.co</div>
                          <div data-id="element-485">hello@metroagency.co</div>
                        </div>
                        <ScanBlock size={36} data-id="element-486" />
                      </div>
                    </div>
                  </TemplateFrame>
                </div>

                <div data-id="element-487">
                  <div className="font-mono text-[10px] text-[var(--text-muted)] mb-4" data-id="element-488">
                    C · NETWORK
                  </div>
                  <TemplateFrame format="A5 Landscape" dimensions="210×148mm" label="Single side" aspect="210/148" maxWidth="max-w-md" data-id="element-489">
                    <div className="w-full h-full bg-[var(--bg-canvas)] p-5 flex flex-col" data-id="element-490">
                      <div className="flex items-start justify-between" data-id="element-491">
                        <img src={METRO_LOGO_URL} alt="me—tro" className="w-16 h-auto" data-id="element-492" />
                        <div className="font-mono text-[8px] text-[var(--text-muted)] text-right leading-relaxed" data-id="element-493">
                          BARCELONA
                          <br data-id="element-494" />
                          2026
                        </div>
                      </div>
                      <div className="flex-1 -mx-2 flex items-center my-2" data-id="element-495">
                        <ServiceNetworkSVG data-id="element-496" />
                      </div>
                      <div className="font-space text-xs font-medium mb-2 leading-tight" data-id="element-497">
                        Capabilities that intersect.{' '}
                        <span className="text-[var(--text-muted)]" data-id="element-498">
                          Projects that route through them.
                        </span>
                      </div>
                      <div className="border-t border-[var(--text-ink)] pt-2 flex items-end justify-between gap-3" data-id="element-499">
                        <div className="font-mono text-[8px] text-[var(--text-muted)] space-y-0.5" data-id="element-500">
                          <div data-id="element-501">metroagency.co</div>
                          <div data-id="element-502">@metroagency</div>
                        </div>
                        <ScanBlock size={36} data-id="element-503" />
                      </div>
                    </div>
                  </TemplateFrame>
                </div>
              </div>
            </div>
          </div>

          {/* 08.5 — Small Service Posters */}
          <div data-id="element-504">
            <div className="flex items-baseline justify-between mb-8" data-id="element-505">
              <div data-id="element-506">
                <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-1" data-id="element-507">
                  08.5 — SHOP POSTER
                </div>
                <h3 className="font-space text-3xl font-medium" data-id="element-508">
                  Small Poster · Service
                </h3>
                <p className="font-helvetica text-sm text-[var(--text-muted)] mt-2 max-w-xl" data-id="element-509">
                  A4 portrait. Catchy from across a small shop. Built as a
                  template — swap the service name, the offer, the price.
                </p>
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] text-right" data-id="element-510">
                A4 · 210×297mm
                <br data-id="element-511" />
                portrait
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-512">
              <TemplateFrame format="A4 Portrait" dimensions="210×297mm" label="A · Headline & Offer" data-id="element-513">
                <div className="w-full h-full bg-[var(--bg-canvas)] p-6 flex flex-col" data-id="element-514">
                  <div className="flex items-start justify-between mb-5" data-id="element-515">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-16 h-auto" data-id="element-516" />
                    <div className="px-2 py-0.5 rounded-full bg-[var(--text-ink)] text-white font-mono text-[8px] tracking-wider" data-id="element-517">
                      SERVICE · 01
                    </div>
                  </div>
                  <div className="font-mono text-[9px] text-[var(--accent-blue)] mb-2 tracking-wider" data-id="element-518">
                    {'{ SERVICE_NAME }'}
                  </div>
                  <div className="font-space font-bold text-[34px] leading-[0.9] tracking-tight mb-4" data-id="element-519">
                    WEB
                    <br data-id="element-520" />
                    DESIGN.
                  </div>
                  <div className="font-helvetica text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-521">
                    Strategy, design, and build under one roof. From the first
                    sketch to launch day.
                  </div>
                  <div className="my-5 py-3 border-y border-[var(--text-ink)]" data-id="element-522">
                    <div className="font-mono text-[8px] text-[var(--text-muted)] mb-1 tracking-wider" data-id="element-523">
                      FROM
                    </div>
                    <div className="font-space font-bold text-2xl leading-none" data-id="element-524">
                      €1,500
                    </div>
                  </div>
                  <div className="mt-auto flex items-end justify-between gap-3" data-id="element-525">
                    <div className="space-y-1" data-id="element-526">
                      <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-wider" data-id="element-527">
                        VISIT
                      </div>
                      <div className="font-space text-sm font-medium" data-id="element-528">
                        metroagency.co
                      </div>
                      <div className="font-helvetica text-[8px] text-[var(--text-muted)]" data-id="element-529">
                        @metroagency
                      </div>
                    </div>
                    <ScanBlock size={48} data-id="element-530" />
                  </div>
                </div>
              </TemplateFrame>

              <TemplateFrame format="A4 Portrait" dimensions="210×297mm" label="B · Vertical Type" data-id="element-531">
                <div className="w-full h-full bg-[var(--accent-blue)] p-6 flex relative overflow-hidden text-white" data-id="element-532">
                  <div className="absolute right-4 top-6 bottom-6 flex items-center" data-id="element-533">
                    <div className="font-space font-bold text-5xl tracking-tighter" style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)'
                  }} data-id="element-534">
                      WEB · DESIGN
                    </div>
                  </div>
                  <div className="flex flex-col w-2/3 z-10" data-id="element-535">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-14 h-auto mb-auto" style={{
                    filter: 'brightness(0) invert(1)'
                  }} data-id="element-536" />
                    <div className="space-y-3 mb-5" data-id="element-537">
                      <div className="px-2 py-0.5 rounded-full bg-white/15 font-mono text-[8px] tracking-wider self-start inline-block w-fit" data-id="element-538">
                        FROM €1,500
                      </div>
                      <div className="font-space text-base font-medium leading-[1.15]" data-id="element-539">
                        A website that
                        <br data-id="element-540" />
                        works as hard as
                        <br data-id="element-541" />
                        you do.
                      </div>
                      <div className="h-px w-8 bg-white" data-id="element-542" />
                      <div className="font-helvetica text-[9px] opacity-80 leading-relaxed" data-id="element-543">
                        Six weeks. Fixed scope.
                        <br data-id="element-544" />
                        Fixed price.
                      </div>
                    </div>
                    <div className="flex items-end gap-3" data-id="element-545">
                      <ScanBlock size={42} dark data-id="element-546" />
                    </div>
                  </div>
                </div>
              </TemplateFrame>

              <TemplateFrame format="A4 Portrait" dimensions="210×297mm" label="C · Question & Answer" data-id="element-547">
                <div className="w-full h-full bg-white p-6 flex flex-col" data-id="element-548">
                  <div className="flex items-start justify-between mb-6" data-id="element-549">
                    <img src={METRO_LOGO_URL} alt="me—tro" className="w-16 h-auto" data-id="element-550" />
                    <div className="w-3 h-3 border-t-2 border-r-2 border-[var(--text-ink)]" data-id="element-551" />
                  </div>
                  <div className="font-mono text-[9px] text-[var(--text-muted)] mb-2 tracking-wider" data-id="element-552">
                    Q —
                  </div>
                  <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight mb-7" data-id="element-553">
                    Need a<br data-id="element-554" />
                    website?
                  </div>
                  <div className="h-px bg-[var(--text-ink)] w-full mb-7" data-id="element-555" />
                  <div className="font-mono text-[9px] text-[var(--accent-blue)] mb-2 tracking-wider" data-id="element-556">
                    A —
                  </div>
                  <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight text-[var(--accent-blue)]" data-id="element-557">
                    We do
                    <br data-id="element-558" />
                    that.
                  </div>
                  <div className="mt-auto pt-5 border-t border-[var(--divider)] flex items-end justify-between gap-3" data-id="element-559">
                    <div className="space-y-1" data-id="element-560">
                      <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-wider" data-id="element-561">
                        VISIT
                      </div>
                      <div className="font-space text-sm font-medium" data-id="element-562">
                        metroagency.co
                      </div>
                      <div className="font-helvetica text-[8px] text-[var(--text-muted)]" data-id="element-563">
                        hello@metroagency.co
                      </div>
                    </div>
                    <ScanBlock size={48} data-id="element-564" />
                  </div>
                </div>
              </TemplateFrame>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}